<template>
  <div class="page-container">
    <h2 class="page-title">用户信息</h2>

    <!-- 搜索栏 -->
    <div class="search-bar">
      <el-form :inline="true">
        <el-form-item label="搜索用户">
          <el-input v-model="searchKey" placeholder="昵称/姓名/手机号" clearable @keyup.enter="handleSearch">
            <template #append>
              <el-button @click="handleSearch">
                <el-icon>
                  <Search />
                </el-icon>
              </el-button>
            </template>
          </el-input>
        </el-form-item>
      </el-form>
    </div>

    <!-- 操作按钮 -->
    <div class="action-bar">
      <el-button type="primary" style="margin-bottom: 10px;" @click="openAddDialog">新增用户</el-button>
    </div>

    <!-- 数据表格 -->
    <el-table :data="tableData" border stripe v-loading="loading">
      <el-table-column prop="nickName" label="昵称" width="150">
        <template #default="{ row }">
          <div class="user-info">
            <el-avatar :size="30" :src="row.avatarUrl" />
            <span class="nickname">{{ row.nickName || '未设置' }}</span>
          </div>
        </template>
      </el-table-column>
      <el-table-column prop="real_name" label="姓名" width="120" />
      <el-table-column prop="phone_number" label="手机号" width="150" />
      <el-table-column label="核销人员" width="120">
        <template #default="{ row }">
          <el-switch v-model="row.verification_personnel" :active-value="1" :inactive-value="0"
            @change="updateVerificationStatus(row)" />
        </template>
      </el-table-column>
      <el-table-column prop="status" label="状态" width="100">
        <template #default="{ row }">
          <el-tag :type="row.status === 2 ? 'success' : 'warning'">
            {{ statusMap[row.status] }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="create_time" label="注册时间" />
      <el-table-column label="操作" fixed="right" width="150">
        <template #default="{ row }">
          <el-button type="primary" size="small" @click="openEditDialog(row)">编辑</el-button>
          <el-popconfirm title="确定删除该用户？" @confirm="handleDelete(row.id)">
            <template #reference>
              <el-button type="danger" size="small">删除</el-button>
            </template>
          </el-popconfirm>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.limit"
      :total="Number(pagination.total)" :page-sizes="[10, 20, 50]" layout="total, sizes, prev, pager, next, jumper"
      @size-change="handlePaginationChange" @current-change="handlePaginationChange" />

    <!-- 新增/编辑弹窗 -->
    <el-dialog v-model="dialogVisible" :title="isEditMode ? '编辑用户' : '新增用户'" width="500px">
      <el-form ref="formRef" :model="formData" :rules="formRules" label-width="80px">
        <el-form-item label="手机号" prop="phone_number">
          <el-input v-model="formData.phone_number" placeholder="请输入手机号" :disabled="isEditMode" />
        </el-form-item>
        <el-form-item label="真实姓名" prop="real_name">
          <el-input v-model="formData.real_name" placeholder="请输入姓名" />
        </el-form-item>
        <el-form-item label="昵称" prop="nickName">
          <el-input v-model="formData.nickName" placeholder="请输入昵称" />
        </el-form-item>
        <el-form-item label="头像" prop="avatarUrl">
          <el-upload class="avatar-uploader" :class="{ 'empty-state': !formData.avatarUrl }" :show-file-list="false"
            :before-upload="beforeAvatarUpload" :http-request="handleCustomUpload" :disabled="uploadLoading">
            <img v-if="formData.avatarUrl" :src="formData.avatarUrl" class="avatar" />
            <div v-else class="empty-container">
              <el-icon class="plus-icon">
                <Plus v-if="!uploadLoading" />
                <Loading v-else class="loading-icon" />
              </el-icon>
            </div>
            <template #tip>
              <div class="el-upload__tip">支持JPG/PNG格式，大小不超过2MB</div>
            </template>
          </el-upload> <!-- 这里修正了结束标签 -->
        </el-form-item>

        <el-form-item label="核销人员" prop="verification_personnel">
          <el-switch v-model="formData.verification_personnel" :active-value="1" :inactive-value="0" />
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="formData.status">
            <el-radio :label="1">待认证</el-radio>
            <el-radio :label="2">已认证</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitForm">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import axios from 'axios'
import Cookies from 'js-cookie'
import config from '@/config'
import { Plus, Loading } from '@element-plus/icons-vue'

import { getUploadSignature, uploadToOSS, beforeUpload } from '@/utils/uploadUtils'

const userInfo = JSON.parse(Cookies.get('user_info') || '{}')
const storeId = userInfo.id
const loading = ref(false)
const searchKey = ref('')
const dialogVisible = ref(false)
const isEditMode = ref(false)
const formRef = ref(null)
const statusMap = { 1: '待认证', 2: '已认证' }

const uploadLoading = ref(false)

// 表格数据
const tableData = ref([])
const pagination = reactive({
  page: 1,
  limit: 10,
  total: 0
})

// 表单数据
const formData = reactive({
  id: null,
  phone_number: '',
  real_name: '',
  nickName: '',
  avatarUrl: '',
  verification_personnel: 0,
  status: 1
})

// 验证规则
const formRules = {
  phone_number: [
    { required: true, pattern: /^1[3-9]\d{9}$/, message: '请输入有效手机号' }
  ],
  real_name: [
    { required: true, message: '请输入姓名', trigger: 'blur' }
  ],
  verification_personnel: [
    { type: 'number', min: 0, max: 1 }
  ]
}

// 修改 fetchData 方法
const fetchData = async () => {
  try {
    loading.value = true
    const params = {
      store_id: storeId,
      page: Number(pagination.page),
      limit: Number(pagination.limit),
      search: searchKey.value
    }

    const { data } = await axios.get(`${config.BASE_URL}/shop/api/users_api.php`, { params })

    if (data.code === 200) {
      tableData.value = data.data.list
      // 强制类型转换保证数据一致性
      pagination.total = Number(data.data.pagination.total)
      pagination.page = Number(data.data.pagination.page)
      pagination.limit = Number(data.data.pagination.limit)
    }
  } catch (error) {
    ElMessage.error('数据加载失败')
  } finally {
    loading.value = false
  }
}

const updateVerificationStatus = async (row) => {
  try {
    await axios.put(`${config.BASE_URL}/shop/api/users_api.php`, {
      id: row.id,
      store_id: storeId,
      verification_personnel: row.verification_personnel
    });
    // 局部更新避免全量刷新
    const target = tableData.value.find(item => item.id === row.id)
    if (target) target.verification_personnel = row.verification_personnel
    ElMessage.success('状态更新成功');
  } catch (error) {
    ElMessage.error('状态更新失败');
    row.verification_personnel = row.verification_personnel === 1 ? 0 : 1 // 回滚状态
  }
}
// 新增分页变化处理
const handlePaginationChange = () => {
  fetchData()
}
// 搜索处理
const handleSearch = () => {
  pagination.page = 1
  fetchData()
}

// 打开新增弹窗
const openAddDialog = () => {
  isEditMode.value = false
  Object.assign(formData, {
    id: null,
    phone_number: '',
    real_name: '',
    nickName: '',
    avatarUrl: '',
    status: 1
  })
  dialogVisible.value = true
}

// 打开编辑弹窗
const openEditDialog = (row) => {
  isEditMode.value = true
  Object.assign(formData, {
    id: row.id,
    phone_number: row.phone_number,
    real_name: row.real_name,
    nickName: row.nickName,
    avatarUrl: row.avatarUrl,
    verification_personnel: Number(row.verification_personnel),
    status: row.status
  })
  dialogVisible.value = true
}
// 添加图片上传处理方法
const beforeAvatarUpload = async (file) => {
  return await beforeUpload(file, { maxSize: 2 })
}

const handleCustomUpload = async (options) => {
  try {
    uploadLoading.value = true

    // 步骤1：获取OSS签名
    const signature = await getUploadSignature('image')

    // 步骤2：上传到OSS
    const result = await uploadToOSS(options.file, signature)

    // 步骤3：更新表单数据
    formData.avatarUrl = result.url

    // 手动触发成功回调（修正这里）
    options.onSuccess({
      data: {
        url: result.url  // 构造符合预期的响应格式
      }
    })

  } catch (error) {
    ElMessage.error(`上传失败: ${error.message}`)
    options.onError(error)
  } finally {
    uploadLoading.value = false
  }
}
// 提交表单
const submitForm = async () => {
  try {
    await formRef.value.validate()

    const payload = {
      ...formData,
      store_id: storeId,
      agent_id: 1 // 根据实际情况获取代理ID
    }

    const url = isEditMode.value
      ? `${config.BASE_URL}/shop/api/users_api.php?id=${formData.id}`
      : `${config.BASE_URL}/shop/api/users_api.php`

    const method = isEditMode.value ? 'put' : 'post'
    await axios[method](url, payload)

    ElMessage.success('操作成功')
    dialogVisible.value = false
    fetchData()
  } catch (error) {
    ElMessage.error(error.response?.data?.message || '操作失败')
  }
}

// 删除用户
const handleDelete = async (id) => {
  try {
    await axios.delete(`${config.BASE_URL}/shop/api/users_api.php`, {
      data: { id, store_id: storeId }
    })
    ElMessage.success('删除成功')
    fetchData()
  } catch (error) {
    ElMessage.error('删除失败')
  }
}

// 初始化加载数据
onMounted(() => {
  fetchData()
})
</script>

<style scoped>
.page-container {

}

.page-title {
  margin-bottom: 20px;
  color: #333;
}

.search-bar {
  margin-bottom: 20px;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 10px;

  .nickname {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.el-pagination {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

/* 优化后的样式 */
.avatar-uploader {
  .empty-container {
    width: 100px;
    height: 100px;
    border: 2px dashed var(--el-border-color);
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: border-color 0.3s;

    &:hover {
      border-color: var(--el-color-primary);
    }

    .plus-icon {
      font-size: 32px;
      color: var(--el-text-color-secondary);
      transition: color 0.3s;
    }

    .loading-icon {
      animation: rotating 2s linear infinite;
      font-size: 28px;
    }
  }

  .avatar {
    width: 100px;
    height: 100px;
    border-radius: 6px;
    object-fit: cover;
    border: 1px solid var(--el-border-color);
  }

  &:hover .empty-container {
    border-color: var(--el-color-primary);

    .plus-icon {
      color: var(--el-color-primary);
    }
  }
}

@keyframes rotating {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}
</style>